/**
 * Applies a menu theme package onto a template's variables/themeVariants.
 *
 * Works for both engines (legacy-scroll and paged-flip) because both resolve
 * colors/fonts through the same variable records. The patch:
 *  - writes the full canonical + derived color set (themes always leave a
 *    template with every standard slot defined),
 *  - recolors template-specific legacy alias keys that are already present
 *    (same push behavior as applyColorPatch in lib/mnrq/guided-config.ts),
 *  - creates both mode variants so every assigned theme adapts to the guest's
 *    light/dark preference, including older templates with no authored pair.
 */

import type { Template, Variables } from '@/app/dashboard/templates/components/types/template';
import { resolveTemplateVariables } from '@/app/dashboard/templates/components/lib/templateVariables';
import { COLOR_ALIASES, FONT_ALIASES } from '@/lib/mnrq/template-variable-standards';
import { DESIGN_COLOR_KEYS, DESIGN_FONT_KEYS, type DesignOverrides } from '@/lib/menu-themes/design-overrides';
import { fonts } from '@/lib/types';
import { deriveMenuThemeColorValues, type MenuThemeColorValues } from './derive';
import {
  resolveMenuThemeColors,
  type MenuThemeColors,
  type MenuThemeMode,
  type MenuThemePackage,
} from './registry';
import { getMenuSkin, mergeSkinColors } from './skins';

/**
 * Font values are stored as `var(--font-*)` strings: legacy nodes, the
 * paged-flip typography, and the public ordering page all pass them straight
 * into font-family, and next/font only registers the loaded families under
 * their CSS variables (bare names like "Playfair Display" do not resolve).
 */
export function menuThemeFontValue(fontName: string): string {
  const entry = fonts.find((font) => font.name === fontName);
  return entry ? `var(--${entry.css})` : fontName;
}

export interface MenuThemeFontValues {
  heading: string;
  body: string;
  display: string;
  logo: string;
  price: string;
}

export function buildThemeFontValues(theme: MenuThemePackage): MenuThemeFontValues {
  const heading = menuThemeFontValue(theme.fonts.heading);
  const body = menuThemeFontValue(theme.fonts.body);
  return {
    heading,
    body,
    display: theme.fonts.display ? menuThemeFontValue(theme.fonts.display) : heading,
    logo: theme.fonts.logo ? menuThemeFontValue(theme.fonts.logo) : heading,
    price: theme.fonts.price ? menuThemeFontValue(theme.fonts.price) : body,
  };
}

type VariableRecord = Record<string, string | number | undefined>;

/**
 * Writes every themed value into `target`, then recolors legacy alias keys
 * (e.g. `chalk`, `headerText`) that exist in `presence` , the merged view of
 * base + variant colors , so template-specific bindings follow the theme too.
 * Aliases that are themselves canonical/derived slots are skipped to avoid
 * cross-contaminating slots the theme sets explicitly (surface vs secondary).
 */
function patchColorRecord(
  target: VariableRecord,
  values: MenuThemeColorValues,
  presence: VariableRecord = target,
  aliasMap: typeof COLOR_ALIASES = COLOR_ALIASES,
) {
  const valueMap = values as unknown as Record<string, string>;
  Object.entries(valueMap).forEach(([key, value]) => {
    target[key] = value;
  });

  Object.entries(aliasMap).forEach(([canonicalKey, aliases]) => {
    const value = valueMap[canonicalKey];
    if (!value) return;
    aliases.forEach((aliasKey) => {
      if (aliasKey === canonicalKey) return;
      if (aliasKey in valueMap) return;
      if (aliasKey in presence) {
        target[aliasKey] = value;
      }
    });
  });
}

function patchFontRecord(
  target: VariableRecord,
  values: Partial<MenuThemeFontValues>,
  presence: VariableRecord = target,
  aliasMap: typeof FONT_ALIASES = FONT_ALIASES,
) {
  const valueMap = values as unknown as Record<string, string>;
  Object.entries(valueMap).forEach(([key, value]) => {
    target[key] = value;
  });

  Object.entries(aliasMap).forEach(([canonicalKey, aliases]) => {
    const value = valueMap[canonicalKey];
    if (!value) return;
    aliases.forEach((aliasKey) => {
      if (aliasKey === canonicalKey) return;
      if (aliasKey in valueMap) return;
      if (aliasKey in presence) {
        target[aliasKey] = value;
      }
    });
  });
}

/**
 * The Design panel's six colour slots and three font slots are deliberately
 * distinct (a solo `accent` change must not repaint `price`, and a `price`
 * font change must not repaint `body`). Themes SHOULD cross-colonize those
 * keys; overrides must not, so their alias map excludes the design slots from
 * being recoloured as someone else's alias while each slot still propagates to
 * template-specific raw keys (itemTitle, headerText, …).
 */
const DESIGN_COLOR_ALIASES: typeof COLOR_ALIASES = Object.fromEntries(
  Object.entries(COLOR_ALIASES).map(([canonicalKey, aliases]) => [
    canonicalKey,
    aliases.filter((aliasKey) => !(DESIGN_COLOR_KEYS as readonly string[]).includes(aliasKey)),
  ]),
) as typeof COLOR_ALIASES;

const DESIGN_FONT_ALIASES: typeof FONT_ALIASES = Object.fromEntries(
  Object.entries(FONT_ALIASES).map(([canonicalKey, aliases]) => [
    canonicalKey,
    aliases.filter((aliasKey) => !(DESIGN_FONT_KEYS as readonly string[]).includes(aliasKey)),
  ]),
) as typeof FONT_ALIASES;

function ensureVariables(template: Template): Variables {
  template.variables = template.variables || {};
  return template.variables;
}

function themedColorValues(theme: MenuThemePackage, mode?: MenuThemeMode): MenuThemeColorValues {
  return deriveMenuThemeColorValues(resolveMenuThemeColors(theme, mode).colors);
}

/**
 * Returns a themed copy of the template.
 *
 * - Base `variables` get the theme's light palette.
 * - Both `themeVariants.light/dark` get mode-specific colors, generating the
 *   missing variant when an older template has only a base palette.
 * - The resolved template follows the guest appearance preference.
 */
export function applyMenuThemeToTemplate(template: Template, theme: MenuThemePackage): Template {
  const cloned = JSON.parse(JSON.stringify(template)) as Template;
  const variables = ensureVariables(cloned);

  const baseMode: MenuThemeMode = 'light';
  const fontValues = buildThemeFontValues(theme);

  variables.colors = { ...(variables.colors || {}) };
  patchColorRecord(variables.colors as VariableRecord, themedColorValues(theme, baseMode));
  variables.fonts = { ...(variables.fonts || {}) };
  patchFontRecord(variables.fonts as VariableRecord, fontValues);

  const patchVariant = (mode: MenuThemeMode) => {
    cloned.themeVariants = cloned.themeVariants || {};
    const variant = cloned.themeVariants[mode] || (cloned.themeVariants[mode] = {});
    variant.colors = { ...(variant.colors || {}) };
    const mergedPresence = {
      ...(variables.colors || {}),
      ...variant.colors,
    } as VariableRecord;
    patchColorRecord(variant.colors as VariableRecord, themedColorValues(theme, mode), mergedPresence);

    variant.fonts = { ...(variant.fonts || {}) };
    const mergedFontPresence = {
      ...(variables.fonts || {}),
      ...variant.fonts,
    } as VariableRecord;
    patchFontRecord(variant.fonts as VariableRecord, fontValues, mergedFontPresence);
  };

  patchVariant('light');
  patchVariant('dark');
  cloned.themeBehavior = 'inherit';

  return cloned;
}

/**
 * Give a public template both appearance modes when it only authored one.
 * Assigned menu themes already do this through `applyMenuThemeToTemplate`;
 * this fallback derives a paired palette from the template's own colors so a
 * guest's page-level appearance choice never leaves an unthemed template body
 * bright inside a dark branch (or vice versa). The template's fonts and layout
 * remain untouched.
 */
export function ensureTemplateAppearanceVariants(template: Template): Template {
  if (template.themeVariants?.light && template.themeVariants?.dark) return template;

  const authoredVariantMode: MenuThemeMode | null = template.themeVariants?.dark && !template.themeVariants?.light
    ? 'dark'
    : template.themeVariants?.light && !template.themeVariants?.dark
      ? 'light'
      : null;
  const sourceMode: MenuThemeMode = authoredVariantMode
    || (template.themeBehavior === 'fixed-dark' ? 'dark' : 'light');
  const source = resolveTemplateVariables(template, sourceMode);
  const colors = source.colors as Record<string, string>;
  const pick = (...values: unknown[]): string | undefined =>
    values.find((value): value is string => typeof value === 'string' && value.trim().length > 0);
  const background = pick(colors.background, colors.canvasBackground, colors.canvas) || '#f6f1e3';
  const surface = pick(colors.surface, colors.panel, colors.card, colors.secondary) || background;
  const divider = pick(colors.divider, colors.panelBorder, colors.border) || 'rgba(26, 24, 20, 0.12)';
  const palette: MenuThemeColors = {
    background,
    surface,
    primary: pick(colors.primary, colors.heading, colors.itemTitle, colors.text) || '#342f2b',
    secondary: pick(colors.secondary, colors.surface, colors.panel) || surface,
    text: pick(colors.text, colors.foreground, colors.body) || '#1a1814',
    muted: pick(colors.muted, colors.mutedForeground, colors.itemDesc) || '#63636b',
    accent: pick(colors.accent, colors.price, colors.primary) || '#8a1f2d',
    divider,
    promoBg: pick(colors.promoBg) || surface,
    promoText: pick(colors.promoText) || pick(colors.text, colors.foreground) || '#1a1814',
    promoBorder: pick(colors.promoBorder) || divider,
    availabilityBg: pick(colors.availabilityBg) || surface,
    availabilityText: pick(colors.availabilityText) || pick(colors.text, colors.foreground) || '#1a1814',
    hero: pick(colors.hero) || background,
    ...(pick(colors.backgroundGradient)
      ? { gradients: { background: pick(colors.backgroundGradient) } }
      : {}),
  };
  const syntheticTheme: MenuThemePackage = {
    id: `template-${template.meta?.slug || 'guest'}`,
    name: 'Template appearance fallback',
    description: 'Generated counterpart for the guest page appearance mode.',
    tags: [],
    fonts: { heading: 'Inter', body: 'Inter' },
    modes: { [sourceMode]: palette },
    defaultMode: sourceMode,
    swatch: { background, surface, text: palette.text, accent: palette.accent },
  };

  const paired = applyMenuThemeToTemplate(template, syntheticTheme);
  const originalBaseFonts = template.variables?.fonts;
  paired.variables = { ...(paired.variables || {}) };
  if (originalBaseFonts) paired.variables.fonts = originalBaseFonts;
  else delete paired.variables.fonts;

  paired.themeVariants = { ...(paired.themeVariants || {}) };
  for (const mode of ['light', 'dark'] as const) {
    const originalFonts = template.themeVariants?.[mode]?.fonts ?? originalBaseFonts;
    const variant = { ...(paired.themeVariants[mode] || {}) };
    if (originalFonts) variant.fonts = originalFonts;
    else delete variant.fonts;
    paired.themeVariants[mode] = variant;
  }

  return paired;
}

/**
 * Applies the menu's `design_overrides` layer (§4.4) — must be called LAST,
 * after any theme package has been applied, so the merchant's tweaks win.
 *
 * Override keys are raw template variable slots (background/surface/primary/
 * text/price/accent; heading/body/price) and are pushed through the same
 * alias recolouring as the theme apply so template-specific bindings follow.
 * Values are written to the base variables AND each existing theme variant so
 * the override holds in light and dark alike.
 *
 * A `skin` id (§2/§5) contributes a *default* colour layer: the skin's colours
 * are merged UNDER any explicit `overrides.colors` (explicit wins), and its
 * `headline`/`decor` are written into `variables` as `headline`/`skinDecor` so
 * a template can opt in via `${headline}`. An unknown/stale skin id is kept as
 * a harmless marker and applies no colours.
 */
export function applyDesignOverrides(template: Template, overrides: DesignOverrides): Template {
  const skin = getMenuSkin(overrides?.skin);
  const mergedColors = skin
    ? mergeSkinColors(skin, overrides?.colors)
    : overrides?.colors;
  const hasColors = Boolean(mergedColors && Object.keys(mergedColors).length > 0);
  const hasFonts = Boolean(overrides?.fonts && Object.keys(overrides.fonts).length > 0);
  if (!hasColors && !hasFonts && !overrides?.skin && !Object.keys(overrides?.templateColors || {}).length && !Object.keys(overrides?.templateFonts || {}).length) return template;

  const cloned = JSON.parse(JSON.stringify(template)) as Template;
  const variables = ensureVariables(cloned);

  const patchVariant = (mode: 'light' | 'dark') => {
    const variant = cloned.themeVariants?.[mode];
    if (!variant) return;

    if (hasColors) {
      variant.colors = { ...(variant.colors || {}) };
      const mergedPresence = {
        ...(variables.colors || {}),
        ...variant.colors,
      } as VariableRecord;
      patchColorRecord(
        variant.colors as VariableRecord,
        mergedColors! as unknown as MenuThemeColorValues,
        mergedPresence,
        DESIGN_COLOR_ALIASES,
      );
    }

    if (hasFonts) {
      variant.fonts = { ...(variant.fonts || {}) };
      const fontPresence = {
        ...(variables.fonts || {}),
        ...variant.fonts,
      } as VariableRecord;
      patchFontRecord(
        variant.fonts as VariableRecord,
        buildFontOverrideValues(overrides.fonts!),
        fontPresence,
        DESIGN_FONT_ALIASES,
      );
    }
  };

  if (hasColors) {
    variables.colors = { ...(variables.colors || {}) };
    patchColorRecord(
      variables.colors as VariableRecord,
      mergedColors! as unknown as MenuThemeColorValues,
      variables.colors as VariableRecord,
      DESIGN_COLOR_ALIASES,
    );
  }

  if (hasFonts) {
    variables.fonts = { ...(variables.fonts || {}) };
    patchFontRecord(
      variables.fonts as VariableRecord,
      buildFontOverrideValues(overrides.fonts!),
      variables.fonts as VariableRecord,
      DESIGN_FONT_ALIASES,
    );
  }

  if (skin) {
    variables.headline = skin.headline;
    variables.skinDecor = skin.decor || '';
  }
  if (overrides?.skin) {
    variables.skin = overrides.skin;
  }

  patchVariant('light');
  patchVariant('dark');

  // Exact authored variables are applied last: editing headerText must not
  // repaint every other text alias. Both render engines consume this record.
  for (const target of [variables, cloned.themeVariants?.light, cloned.themeVariants?.dark]) {
    if (!target) continue;
    target.colors = { ...target.colors, ...overrides.templateColors };
    target.fonts = { ...target.fonts, ...Object.fromEntries(Object.entries(overrides.templateFonts || {}).map(([key, name]) => [key, menuThemeFontValue(name)])) };
  }

  return cloned;
}

/**
 * Only overridden slots are emitted (a `price` tweak must not clobber the
 * template's real heading/body). `patchFontRecord` also aliases `heading` →
 * display/logo for bindings that reference those raw keys.
 */
function buildFontOverrideValues(
  fontsOverride: NonNullable<DesignOverrides['fonts']>,
): Partial<MenuThemeFontValues> {
  const out: Partial<MenuThemeFontValues> = {};
  if (fontsOverride.heading) {
    const value = menuThemeFontValue(fontsOverride.heading);
    out.heading = value;
  }
  if (fontsOverride.body) {
    out.body = menuThemeFontValue(fontsOverride.body);
  }
  if (fontsOverride.display) out.display = menuThemeFontValue(fontsOverride.display);
  if (fontsOverride.logo) out.logo = menuThemeFontValue(fontsOverride.logo);
  if (fontsOverride.price) {
    out.price = menuThemeFontValue(fontsOverride.price);
  }
  return out;
}
